import React, { useState, useEffect } from "react";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Image, Upload, message, Spin, Tooltip, Button } from "antd";
import { UploadFile as AntUploadFile, UploadProps } from "antd";
import { Document } from "../../../types";
import { useAppDispatch } from "../../../utils/hooks/redux";
import { deleteDocument, uploadDocument } from "../../../redux/features/report/reportAsyncThunk";
import ConfirmationModal from "../../../components/ConfirmationModal"; // Import your ConfirmationModal

interface UploadFile extends AntUploadFile {
    isDeleted?: boolean;
    isUpdated?: boolean;
}

type FileType = NonNullable<UploadFile["originFileObj"]>;

const getBase64 = (file: FileType): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = (error) => reject(error);
    });

interface ImagesProps {
    documents: Document[];
    id: string | number;
    updatedDocuments?: Document[];
    showUpdated?: boolean;
    showEditButtons?: boolean;
}

const Images: React.FC<ImagesProps> = ({ documents, id, updatedDocuments = [], showUpdated = false, showEditButtons = false }) => {
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState("");
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    // const [loading, setLoading] = useState(false);
    // const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    // const [fileToDelete, setFileToDelete] = useState<UploadFile | null>(null);

    const dispatch = useAppDispatch();

    useEffect(() => {
        // Memoize the document transformations to prevent unnecessary recalculations
        const transformDocuments = () => {
            const currentDocs = documents?.map((doc) => ({
                uid: String(doc.id),
                name: doc.documentName,
                status: "done" as const,
                url: doc.fullUrl,
                isDeleted: false
            })) || [];

            const updatedDocs = updatedDocuments?.map((doc) => ({
                uid: String(doc.id),
                name: doc.documentName,
                status: "done" as const,
                url: doc.fullUrl,
                isDeleted: false,
                isUpdated: true
            })) || [];

            // If showing updated view, show only updated documents
            if (showUpdated && updatedDocuments?.length > 0) {
                return updatedDocs;
            }

            // For original view, show original documents and mark updated ones
            const combinedDocs = [...currentDocs];
            updatedDocs.forEach(updatedDoc => {
                const existingIndex = combinedDocs.findIndex(doc => doc.uid === updatedDoc.uid);
                if (existingIndex >= 0) {
                    combinedDocs[existingIndex] = {
                        ...combinedDocs[existingIndex],
                        isDeleted: true
                    };
                }
            });
            return combinedDocs;
        };

        // Compare current fileList with new transformed documents
        const newFileList = transformDocuments();
        const hasChanged = JSON.stringify(newFileList) !== JSON.stringify(fileList);

        if (hasChanged) {
            setFileList(newFileList);
        }
    }, [documents, updatedDocuments, showUpdated, showEditButtons]); // Only depend on input props

    const handlePreview = async (file: UploadFile) => {
        try {
            if (!file.url && !file.preview) {
                file.preview = await getBase64(file.originFileObj as FileType);
            }
            setPreviewImage(file.url || (file.preview as string));
            setPreviewOpen(true);
        } catch (error) {
            message.error("Failed to preview the image.");
        }
    };


    // const handleDeleteConfirm = async () => {
    //     if (fileToDelete) {
    //         try {
    //             setLoading(true);
    //             await dispatch(deleteDocument({ id: fileToDelete.uid })).unwrap();
    //             setFileList((prevFileList) =>
    //                 fileList.filter((file) => file.uid !== fileToDelete.uid)
    //             );
    //             // message.success("File deleted successfully.");
    //         } catch (error: any) {
    //             message.error(error?.message || error || "File deletion failed. Please try again.");
    //         } finally {
    //             setLoading(false);
    //             setShowDeleteConfirm(false);
    //             setFileToDelete(null);
    //         }
    //     }
    // };

    // const handleRemove = (file: UploadFile) => {
    //     setFileToDelete(file);
    //     setShowDeleteConfirm(true);
    // };

    // const handleFileChange = async ({ file, fileList }: { file: UploadFile, fileList: UploadFile[] }) => {
    //     if (file.status === 'uploading') {
    //         setLoading(true);
    //         return;
    //     }
    //     if (file.status === 'done') {
    //         setLoading(false);
    //         // Only update fileList when upload is complete
    //         setFileList(fileList);
    //     }
    //     if (file.status === 'error') {
    //         setLoading(false);
    //         message.error(`${file.name} file upload failed.`);
    //     }
    // };

    // const handleBeforeUpload = (file: File): boolean => {
    //     const isImg = /^image\/(jpeg|jpg|png|gif|webp)$/.test(file.type);
    //     if (!isImg) {
    //         message.error(`${file.name} You can only upload JPG/PNG/JPEG/WEBP files!`);
    //         return false;
    //     }

    //     const isLt1M = file.size / 1024 / 1024 <= 1;
    //     if (!isLt1M) {
    //         message.error(`${file.name} Image must be smaller than 1MB!`);
    //         return false;
    //     }
    //     return true;
    // };

    // const uploadButton = (
    //     <div>
    //         {loading ? (
    //             <Spin />
    //         ) : (
    //             <Tooltip title={"Upload"}>
    //                 <PlusOutlined />
    //                 <div style={{ marginTop: 8 }}>Upload</div>
    //             </Tooltip>
    //         )}
    //     </div>
    // );

    return (
        <>
            <Upload
                disabled={true}
                listType="picture-card"
                fileList={fileList}
                onPreview={handlePreview}
                // onChange={handleFileChange}
                // onRemove={handleRemove}
                // beforeUpload={handleBeforeUpload}
                // multiple={true}
                // customRequest={async ({ file, onSuccess, onError }) => {
                //     try {
                //         const formData = new FormData();
                //         formData.append("files[]", file as File);
                //         await dispatch(uploadDocument({ id, formData })).unwrap();
                //         onSuccess?.(null);
                //     } catch (error: any) {
                //         onError?.(error);
                //         message.error(error.message || "File upload failed");
                //     } finally {
                //         setLoading(false);
                //     }
                // }}
                itemRender={(originNode, file: UploadFile) => (
                    <div style={{
                        border: showUpdated && file.isUpdated ? '2px solid green' : 'none',
                        overflow: 'hidden',
                        width: '100%',
                        height: '100%',
                        position: 'relative'
                    }}>
                        {file.isUpdated && showUpdated && (
                            <div style={{
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                background: 'green',
                                color: 'white',
                                padding: '2px 6px',
                                fontSize: '12px',
                                zIndex: 1
                            }}>
                                New
                            </div>
                        )}
                        {originNode}
                    </div>
                )}
            >
                {/* {showEditButtons && fileList.length < 3 && uploadButton} */}
            </Upload>
            {previewImage && (
                <Image
                    wrapperStyle={{ display: "none" }}
                    preview={{
                        visible: previewOpen,
                        onVisibleChange: (visible) => setPreviewOpen(visible),
                        afterOpenChange: (visible) => !visible && setPreviewImage(""),
                    }}
                    src={previewImage}
                />
            )}
            {/* {showDeleteConfirm && (
                <ConfirmationModal
                    handleSave={handleDeleteConfirm}
                    isModalOpen={!!showDeleteConfirm}
                    onCancel={() => !loading && setShowDeleteConfirm(false)}
                    title={"Confirm Delete?"}
                    message={`Are you sure you want to delete this file?`}
                    confirmButtonText="Delete"
                    loading={loading}
                />
            )} */}
            {/* {showEditButtons && (
                <p style={{ marginBottom: "0px" }}>You can upload up to 3 files (JPEG, JPG, PNG, WEBP), each less than 1MB.</p>
            )} */}
        </>
    );
};

export default Images;
